import * as React from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { NavMenuOption, PageLayout } from "../../components/PageLayout/PageLayout";
import {
  ClientSummaryCollection,
  ClientsService,
  ApiError,
  ClientSummary,
  SystemSettingsService,
  SystemSettings
} from "../../gen/clients";
import { ApiErrorMessage } from "../../components/ApiErrorMessage/ApiErrorMessage";
import { DataTable } from "../../components/DataTable/DataTable";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CssBaseline,
  IconButton,
  InputBase,
  Paper,
  TablePagination,
  Grid,
  Stack,
  Toolbar,
  Typography
} from "@mui/material";
import { Form, Formik } from "formik";
import { useDialogState } from "hooks/useDialogState";
import { PagePath } from "../../types/PagePath";
import Link from "@mui/material/Link";
import { SelectOneField } from "../../components/formikFields/SelectOneField/SelectOneField";
import { SelectOption } from "@mui/base";
import { XtrfSyncStatus } from "./XtrfSyncStatus";
import { SsoProvidersPanel } from "./SsoProvidersPanel";
import { getRestrictedBranchId } from "../../utils/lltsBranchRestrictionUtils";

const ALL_OPTION_VALUE = "ALL";

const FORCED_BRANCH_ID: string | undefined = getRestrictedBranchId();

const ClientsPage: React.FC = () => {
  const history = useHistory();
  const [ssoProvidersPanelOpen, openSsoProvidersPanel, closeSsoProvidersPanel] = useDialogState();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [companyNameSearch, setCompanyNameSearch] = React.useState("");
  const [debouncedCompanyNameSearch, setDebouncedCompanyNameSearch] = React.useState("");
  const [selectedBranchId, setSelectedBranchId] = React.useState<string>(
    FORCED_BRANCH_ID !== undefined ? FORCED_BRANCH_ID : ALL_OPTION_VALUE
  );

  // Debounce effect for company name search
  React.useEffect(() => {
    // If search is cleared, trigger immediately for better UX
    if (companyNameSearch === "") {
      setDebouncedCompanyNameSearch("");
      return;
    }
    // Otherwise, debounce with 1s delay
    const handler = setTimeout(() => {
      setDebouncedCompanyNameSearch(companyNameSearch);
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [companyNameSearch]);

  // Reset page when search changes
  React.useEffect(() => {
    setPage(0);
  }, [debouncedCompanyNameSearch, selectedBranchId]);

  // Fetch system settings to get branches
  const { data: systemSettings, isLoading: isLoadingSettings } = useQuery<SystemSettings, ApiError>(
    ["getSystemSettings"],
    {
      queryFn: SystemSettingsService.getSystemSettings,
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  // Sort branches alphabetically by name
  const sortedBranches = React.useMemo(() => {
    if (!systemSettings?.lltsBranches) {
      return [];
    }
    return [...systemSettings.lltsBranches].sort((a, b) => a.name.localeCompare(b.name));
  }, [systemSettings?.lltsBranches]);

  // Create branch options for dropdown
  const branchOptions: SelectOption<string>[] = React.useMemo(() => {
    const options: SelectOption<string>[] = [{ value: ALL_OPTION_VALUE, label: "All" }];

    sortedBranches.forEach(branch => {
      options.push({
        value: branch.id,
        label: branch.name
      });
    });

    return options;
  }, [sortedBranches]);

  const selectedBranchXtId = React.useMemo(() => {
    if (selectedBranchId === ALL_OPTION_VALUE) {
      return undefined;
    }
    return sortedBranches.find(branch => branch.id === selectedBranchId)?.xtrfId;
  }, [selectedBranchId, sortedBranches]);

  const { data, isLoading, isFetching, error, refetch } = useQuery<ClientSummaryCollection, ApiError>(
    ["listClients", page, rowsPerPage, debouncedCompanyNameSearch, selectedBranchXtId],
    {
      queryFn: () =>
        ClientsService.listClients({
          page: page + 1,
          limit: rowsPerPage,
          companyName: debouncedCompanyNameSearch || undefined,
          branch: selectedBranchXtId
        }),
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  const onRowClick = React.useCallback(
    (client: ClientSummary) => {
      history.push(`${PagePath.clients}/${client.id}`);
    },
    [history]
  );

  const clientIdCell = React.useCallback(
    (client: ClientSummary) => (
      <Link onClick={e => e.preventDefault()} href={`/clients/${client.id}`}>
        {client.id}
      </Link>
    ),
    []
  );

  const clientNameCell = React.useCallback((client: ClientSummary) => client.companyName, []);

  const branchCell = React.useCallback((client: ClientSummary) => client.branchName, []);

  const rowKey = React.useCallback((client: ClientSummary) => client.id, []);

  const onPageChange = React.useCallback((event, selectedPage: number) => {
    setPage(selectedPage);
  }, []);

  const onRowsPerPageChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
  }, []);

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.CLIENTS}
      title={
        <Toolbar disableGutters variant="dense">
          <Box flexGrow={1}>
            <Typography variant="h4">Clients</Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <XtrfSyncStatus />
            <Button variant="outlined" color="primary" type="button" onClick={openSsoProvidersPanel}>
              View SSO Providers
            </Button>
          </Stack>
        </Toolbar>
      }
    >
      <CssBaseline />
      <Formik
        initialValues={{ branch: FORCED_BRANCH_ID !== undefined ? FORCED_BRANCH_ID : ALL_OPTION_VALUE }}
        onSubmit={() => refetch()}
      >
        <Form>
          <Grid container spacing={2} sx={{ my: 2 }}>
            <Grid item xs={12} sm={4} md={4} xl={3}>
              <SelectOneField
                name="branch"
                label="Branch"
                options={branchOptions}
                isLoading={isLoadingSettings}
                disabled={FORCED_BRANCH_ID !== undefined}
                onChange={value => {
                  setSelectedBranchId(value);
                  refetch();
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={8} md={5} xl={4}>
              <Paper sx={{ p: "2px 4px", display: "flex", alignItems: "center" }}>
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search by client name or ID"
                  value={companyNameSearch}
                  onChange={event => setCompanyNameSearch(event.currentTarget.value)}
                />
                <IconButton type="submit" sx={{ p: "10px" }} aria-label="search">
                  <SearchIcon />
                </IconButton>
              </Paper>
            </Grid>
          </Grid>
        </Form>
      </Formik>
      <DataTable
        columns={[
          {
            id: "clientId",
            title: "XTRF Client ID",
            cell: clientIdCell
          },
          {
            id: "clientName",
            title: "Name",
            cell: clientNameCell
          },
          {
            id: "branch",
            title: "LanguageLine Branch",
            cell: branchCell
          }
        ]}
        data={data?.items || []}
        isLoading={isLoading || isFetching}
        error={error && <ApiErrorMessage apiError={error} />}
        rowKey={rowKey}
        onRowClick={onRowClick}
      />

      {data?.totalResults !== undefined && data?.totalResults > 0 && (
        <TablePagination
          component="div"
          count={data.totalResults}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}

      {ssoProvidersPanelOpen && <SsoProvidersPanel onClose={closeSsoProvidersPanel} />}
    </PageLayout>
  );
};

export { ClientsPage };
